<script setup lang="ts">
import { OptionField } from "@/core/interfaces/flowchart/flowchart";
import { Label } from "@/core/components/ui/label";
import { Input } from "@/core/components/ui/input";
import { Checkbox } from "@/core/components/ui/checkbox";
import BaseRadio from "@/core/components/base/BaseRadio.vue";

defineProps<{
    field: OptionField;
    fieldSlug: string;
}>();

const model = defineModel<string | string[]>({required: true});

/**
 * Handle single choice change
 */
const handleSingleChoiceChange = (value: string) => {
    model.value = value;
};

/**
 * Handle text input change
 */
const handleTextChange = (event: Event) => {
    const target = event.target as HTMLInputElement;

    model.value = target.value;
};

/**
 * Handle multiple choice change
 */
const handleMultipleChoiceChange = (slug: string, checked: boolean) => {
    const currentValues = Array.isArray(model.value) ? model.value : [];

    if (checked) {
        model.value = [...currentValues, slug];
    } else {
        model.value = currentValues.filter(v => v !== slug);
    }
};

/**
 * Check if a value is selected in multiple choice
 */
const isChecked = (slug: string): boolean => {
    return Array.isArray(model.value) && model.value.includes(slug);
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <Label class="font-semibold">{{ field.label }}</Label>
    <div v-if="field.type === 'single_choice' && field.values">
      <BaseRadio
        v-for="value in field.values"
        :key="value.slug"
        :label="value.label"
        :value="value.slug"
        :model-value="typeof modelValue === 'string' ? modelValue : ''"
        @update:model-value="handleSingleChoiceChange"
      />
    </div>
    <Input
      v-else-if="field.type === 'text'"
      :model-value="typeof modelValue === 'string' ? modelValue : ''"
      type="text"
      @input="handleTextChange"
    />
    <div
      v-else-if="field.type === 'multiple_choice' && field.values"
      class="flex flex-col gap-3"
    >
      <div
        v-for="value in field.values"
        :key="value.slug"
        class="flex items-start space-x-2"
      >
        <Checkbox
          :id="`${fieldSlug}-${value.slug}`"
          :model-value="isChecked(value.slug)"
          @update:model-value="(checked) => handleMultipleChoiceChange(value.slug, checked as boolean)"
        />
        <Label :for="`${fieldSlug}-${value.slug}`" class="font-normal cursor-pointer">
          {{ value.label }}
        </Label>
      </div>
    </div>
  </div>
</template>
